// File: src/components/Maintenance/MaintenanceBoard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench,
  ClipboardList,
  BarChart3,
  Wallet,
  ArrowRight,
  Activity,
  PauseCircle,
  PlusCircle,
  Settings2,
  Search,
  LayoutDashboard,
  FilePlus2,
  ListTodo,
  ReceiptText,
} from "lucide-react";
import api from "@/lib/axios";
import MaintenanceSummary from "./MaintenanceSummary";
import MaintenanceCostTable from "./MaintenanceCostTable";
import AddTaskModal from "./AddTaskModal";
import TaskDetailModal from "./TaskDetailModal";
import SkeletonTaskRow from "./SkeletonTaskRow";
import TaskTableToolbar from "./TaskTableToolbar";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const toArray = (v) =>
  Array.isArray(v)
    ? v
    : Array.isArray(v?.data)
      ? v.data
      : Array.isArray(v?.tasks)
        ? v.tasks
        : Array.isArray(v?.items)
          ? v.items
          : [];

const STATUS_OPTIONS = [
  "all",
  "Open",
  "Submitted",
  "Under Review",
  "Approved",
  "Assigned",
  "In Progress",
  "On Hold",
  "Waiting Parts",
  "Waiting Vendor",
  "Completed",
  "Closed",
  "Cancelled",
];

const DASHBOARD_VIEWS = {
  dashboard: "dashboard",
  submit: "submit",
  tasks: "tasks",
  summary: "summary",
  costs: "costs",
};

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isFinite(n)
    ? new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n)
    : "£0.00";
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusBadgeClass = (status) => {
  const s = String(status || "").toLowerCase();

  if (["closed", "completed"].includes(s)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (["approved", "assigned", "in progress"].includes(s)) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }

  if (["on hold", "waiting parts", "waiting vendor"].includes(s)) {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (["submitted", "under review"].includes(s)) {
    return "border-violet-200 bg-violet-50 text-violet-700";
  }

  if (["cancelled"].includes(s)) {
    return "border-slate-200 bg-slate-100 text-slate-700";
  }

  return "border-rose-200 bg-rose-50 text-rose-700";
};

const priorityBadgeClass = (priority) => {
  const p = String(priority || "").toLowerCase();

  if (p === "high") return "border-rose-200 bg-rose-50 text-rose-700";
  if (p === "medium") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-100 text-slate-700";
};

const topNavButtonClass = (isActive) =>
  [
    "h-16 justify-start rounded-2xl border px-5 text-left shadow-sm transition-all duration-200",
    "flex items-center gap-3 font-semibold",
    isActive
      ? "border-slate-900 bg-slate-950 text-white hover:bg-slate-900"
      : "border-slate-300 bg-white text-slate-900 hover:bg-slate-50 hover:border-slate-400",
  ].join(" ");

const SectionCard = ({ title, subtitle, rightSlot, children, className = "" }) => (
  <div className={`rounded-3xl border border-slate-200 bg-white shadow-sm ${className}`}>
    <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
      <div>
        <h2 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      {rightSlot ? <div>{rightSlot}</div> : null}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const StatCard = ({
  title,
  value,
  hint,
  onClick,
  accent = "blue",
  icon,
}) => {
  const accentMap = {
    blue: "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-50/50",
    emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/50",
    amber: "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-amber-50/50",
    violet: "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-violet-50/50",
    rose: "border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-50/50",
    slate: "border-slate-200 bg-gradient-to-br from-slate-50 via-white to-slate-50/50",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full rounded-3xl border p-5 text-left shadow-sm transition duration-200 hover:-translate-y-0.5 hover:shadow-md ${accentMap[accent] || accentMap.blue}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-medium text-slate-700">{title}</div>
        <div className="rounded-2xl border border-white/80 bg-white/80 p-2 text-slate-700 shadow-sm">
          {icon}
        </div>
      </div>

      <div className="mt-5 text-3xl font-bold tracking-tight text-slate-950">{value}</div>
      <div className="mt-2 text-xs leading-5 text-slate-600">{hint}</div>
    </button>
  );
};

const ActionTile = ({ eyebrow, title, description, buttonText, onClick, tone = "blue", icon }) => {
  const toneMap = {
    blue: "border-blue-200 bg-gradient-to-br from-blue-50 to-white",
    emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white",
    amber: "border-amber-200 bg-gradient-to-br from-amber-50 to-white",
    violet: "border-violet-200 bg-gradient-to-br from-violet-50 to-white",
    slate: "border-slate-200 bg-gradient-to-br from-slate-50 to-white",
  };

  return (
    <div className={`rounded-3xl border p-6 shadow-sm ${toneMap[tone] || toneMap.blue}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          {eyebrow ? (
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
              {eyebrow}
            </div>
          ) : null}

          <h3 className="mt-2 text-lg font-semibold tracking-tight text-slate-950">{title}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-700">{description}</p>
        </div>

        <div className="rounded-2xl border border-white/80 bg-white/80 p-3 text-slate-700 shadow-sm">
          {icon}
        </div>
      </div>

      <div className="mt-5">
        <Button onClick={onClick} className="gap-2">
          {buttonText}
          <ArrowRight size={15} />
        </Button>
      </div>
    </div>
  );
};

const EmptyPanel = ({ title, text }) => (
  <div className="flex min-h-[220px] items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
    <div>
      <div className="text-base font-semibold text-slate-900">{title}</div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
    </div>
  </div>
);

const MaintenanceBoard = () => {
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshFlag, setRefreshFlag] = useState(false);

  const [activeView, setActiveView] = useState(DASHBOARD_VIEWS.dashboard);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updates, setUpdates] = useState({});

  useEffect(() => {
    let cancelled = false;

    const fetchTasks = async () => {
      setLoading(true);

      try {
        const params = {};
        if (filter && filter !== "all") params.status = filter;
        if (search?.trim()) params.q = search.trim();
        params.page_size = 200;

        const res = await api.get("/solo-mdsh-api/maintenance/tasks", { params });

        if (!cancelled) {
          setTasks(toArray(res.data));
        }
      } catch (err) {
        console.error("Failed to fetch maintenance tasks:", err);
        if (!cancelled) {
          setTasks([]);
          toast.error("Failed to load maintenance tasks");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (
      activeView === DASHBOARD_VIEWS.dashboard ||
      activeView === DASHBOARD_VIEWS.tasks
    ) {
      fetchTasks();
    }

    return () => {
      cancelled = true;
    };
  }, [refreshFlag, filter, search, activeView]);

  const handleInlineChange = (taskId, field, value) => {
    setUpdates((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
  };

  const handleSave = async (taskId) => {
    const payload = updates[taskId] || {};

    try {
      const res = await api.put(`/solo-mdsh-api/maintenance/update/${taskId}`, {
        status: payload.status,
        update_notes: payload.update_notes,
        actual_cost: payload.actual_cost === "" ? undefined : payload.actual_cost,
      });

      toast.success("Task updated successfully");

      setUpdates((prev) => {
        const copy = { ...prev };
        delete copy[taskId];
        return copy;
      });

      setTasks((prev) =>
        prev.map((item) => (item.task_id === taskId ? { ...item, ...res.data } : item))
      );

      setRefreshFlag((f) => !f);
    } catch (err) {
      console.error("Maintenance update failed:", err);
      toast.error(err?.response?.data?.error || "Failed to update task");
    }
  };

  const normalizedFilter = String(filter || "all").toLowerCase();

  const filteredTasks = useMemo(() => {
    const list = toArray(tasks);

    return list.filter((task) => {
      const matchesStatus =
        normalizedFilter === "all"
          ? true
          : String(task?.status || "").toLowerCase() === normalizedFilter;

      if (!matchesStatus) return false;

      if (!search.trim()) return true;

      const needle = search.trim().toLowerCase();

      return [
        task?.task_number,
        task?.issue_type,
        task?.description,
        task?.location,
        task?.asset_number,
        task?.vendor_name,
        task?.po_number,
        task?.reported_by_name,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle));
    });
  }, [tasks, normalizedFilter, search]);

  const dashboardStats = useMemo(() => {
    const list = toArray(tasks);

    const openLikeStatuses = new Set([
      "open",
      "submitted",
      "under review",
      "approved",
      "assigned",
    ]);

    const progressStatuses = new Set([
      "in progress",
      "waiting parts",
      "waiting vendor",
    ]);

    const holdStatuses = new Set(["on hold"]);
    const closedStatuses = new Set(["closed", "completed", "cancelled"]);

    let openCount = 0;
    let progressCount = 0;
    let holdCount = 0;
    let closedCount = 0;
    let totalActual = 0;

    for (const item of list) {
      const status = String(item?.status || "").toLowerCase();

      if (openLikeStatuses.has(status)) openCount += 1;
      if (progressStatuses.has(status)) progressCount += 1;
      if (holdStatuses.has(status)) holdCount += 1;
      if (closedStatuses.has(status)) closedCount += 1;

      totalActual += Number(item?.actual_cost || 0);
    }

    return {
      total: list.length,
      openCount,
      progressCount,
      holdCount,
      closedCount,
      totalActual,
    };
  }, [tasks]);

  const recentTasks = useMemo(() => {
    return [...filteredTasks]
      .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))
      .slice(0, 6);
  }, [filteredTasks]);

  return (
    <div className="min-h-full bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.08),_transparent_28%),linear-gradient(to_bottom,_#f8fbff,_#f8fafc_30%,_#f1f5f9_100%)] px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1680px] space-y-6">
        <div className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
          <div className="relative">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(59,130,246,0.10),_transparent_30%),radial-gradient(circle_at_bottom_left,_rgba(16,185,129,0.08),_transparent_28%)]" />
            <div className="relative px-6 py-7 md:px-8 md:py-8">
              <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                <div className="max-w-4xl">
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                    <Wrench size={14} />
                    Maintenance Phase 1
                  </div>

                  <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-950 md:text-4xl">
                    Maintenance Dashboard
                  </h1>

                  <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-700 md:text-[15px]">
                    Start from the operational dashboard, monitor live task flow, register new work,
                    review the task register, and move into the dedicated management workspace only
                    when assignment, cost control, procurement linkage, or closure is required.
                  </p>
                </div>

                <div className="grid w-full gap-3 sm:grid-cols-2 xl:w-auto xl:grid-cols-2 2xl:grid-cols-3">
                  <Button
                    onClick={() => setActiveView(DASHBOARD_VIEWS.dashboard)}
                    className={topNavButtonClass(activeView === DASHBOARD_VIEWS.dashboard)}
                  >
                    <LayoutDashboard size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Dashboard
                    </span>
                  </Button>

                  <Button
                    onClick={() => setActiveView(DASHBOARD_VIEWS.submit)}
                    className={topNavButtonClass(activeView === DASHBOARD_VIEWS.submit)}
                  >
                    <FilePlus2 size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Report Task
                    </span>
                  </Button>

                  <Button
                    onClick={() => setActiveView(DASHBOARD_VIEWS.tasks)}
                    className={topNavButtonClass(activeView === DASHBOARD_VIEWS.tasks)}
                  >
                    <ListTodo size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Task Register
                    </span>
                  </Button>

                  <Button
                    onClick={() => navigate("/maintenance/manage")}
                    className={topNavButtonClass(false)}
                  >
                    <Wrench size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Management
                    </span>
                  </Button>

                  <Button
                    onClick={() => setActiveView(DASHBOARD_VIEWS.summary)}
                    className={topNavButtonClass(activeView === DASHBOARD_VIEWS.summary)}
                  >
                    <BarChart3 size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Summary
                    </span>
                  </Button>

                  <Button
                    onClick={() => setActiveView(DASHBOARD_VIEWS.costs)}
                    className={topNavButtonClass(activeView === DASHBOARD_VIEWS.costs)}
                  >
                    <ReceiptText size={18} className="shrink-0" />
                    <span className="text-sm font-semibold">
                      Costs
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {activeView === DASHBOARD_VIEWS.submit && (
          <SectionCard
            title="Report Maintenance Task"
            subtitle="Create a new operational maintenance request."
          >
            <AddTaskModal
              onSuccess={() => {
                setRefreshFlag((f) => !f);
                setActiveView(DASHBOARD_VIEWS.tasks);
              }}
            />
          </SectionCard>
        )}

        {activeView === DASHBOARD_VIEWS.summary && (
          <SectionCard
            title="Maintenance Summary"
            subtitle="Operational summary and maintenance reporting view."
          >
            <MaintenanceSummary />
          </SectionCard>
        )}

        {activeView === DASHBOARD_VIEWS.costs && (
          <SectionCard
            title="Maintenance Costs"
            subtitle="Review recorded maintenance cost activity."
          >
            <MaintenanceCostTable />
          </SectionCard>
        )}

        {activeView === DASHBOARD_VIEWS.dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-5">
              <StatCard
                title="Total Tasks"
                value={dashboardStats.total}
                hint="All visible maintenance requests in the current data set."
                accent="blue"
                icon={<ClipboardList size={18} />}
                onClick={() => setActiveView(DASHBOARD_VIEWS.tasks)}
              />

              <StatCard
                title="Open Pipeline"
                value={dashboardStats.openCount}
                hint="Open, submitted, under review, approved, and assigned work."
                accent="violet"
                icon={<Activity size={18} />}
                onClick={() => {
                  setFilter("Open");
                  setActiveView(DASHBOARD_VIEWS.tasks);
                }}
              />

              <StatCard
                title="In Progress"
                value={dashboardStats.progressCount}
                hint="Active operational work currently moving through execution."
                accent="amber"
                icon={<Settings2 size={18} />}
                onClick={() => {
                  setFilter("In Progress");
                  setActiveView(DASHBOARD_VIEWS.tasks);
                }}
              />

              <StatCard
                title="On Hold"
                value={dashboardStats.holdCount}
                hint="Blocked or paused work items that may need management action."
                accent="rose"
                icon={<PauseCircle size={18} />}
                onClick={() => {
                  setFilter("On Hold");
                  setActiveView(DASHBOARD_VIEWS.tasks);
                }}
              />

              <StatCard
                title="Recorded Actual Cost"
                value={formatMoney(dashboardStats.totalActual)}
                hint="Current actual cost total from visible maintenance tasks."
                accent="emerald"
                icon={<Wallet size={18} />}
                onClick={() => setActiveView(DASHBOARD_VIEWS.costs)}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[1.45fr_0.9fr]">
              <SectionCard
                title="Recent Task Activity"
                subtitle="Latest maintenance requests and current workflow visibility."
                rightSlot={
                  <Button
                    variant="outline"
                    onClick={() => setActiveView(DASHBOARD_VIEWS.tasks)}
                    className="gap-2 text-slate-900"
                  >
                    Open Register
                    <ArrowRight size={15} />
                  </Button>
                }
              >
                <div className="overflow-hidden rounded-3xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50/90">
                        <tr className="text-left text-slate-700">
                          <th className="px-5 py-4 font-semibold">Task</th>
                          <th className="px-5 py-4 font-semibold">Status</th>
                          <th className="px-5 py-4 font-semibold">Priority</th>
                          <th className="px-5 py-4 font-semibold">Asset</th>
                          <th className="px-5 py-4 font-semibold">Created</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-slate-200 bg-white">
                        {loading ? (
                          [...Array(5)].map((_, i) => (
                            <tr key={i}>
                              <td colSpan={5}>
                                <SkeletonTaskRow />
                              </td>
                            </tr>
                          ))
                        ) : recentTasks.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-5 py-12">
                              <EmptyPanel
                                title="No maintenance tasks yet"
                                text="There are currently no recent maintenance tasks to display."
                              />
                            </td>
                          </tr>
                        ) : (
                          recentTasks.map((task) => (
                            <tr key={task.task_id} className="transition hover:bg-slate-50">
                              <td className="px-5 py-4">
                                <div className="font-semibold text-slate-900">
                                  {task.issue_type || "—"}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                  #{task.task_id}
                                  {task.task_number ? ` • ${task.task_number}` : ""}
                                </div>
                              </td>

                              <td className="px-5 py-4">
                                <Badge className={`border ${statusBadgeClass(task.status)}`}>
                                  {task.status || "—"}
                                </Badge>
                              </td>

                              <td className="px-5 py-4">
                                <Badge className={`border ${priorityBadgeClass(task.priority)}`}>
                                  {task.priority || "—"}
                                </Badge>
                              </td>

                              <td className="px-5 py-4 text-slate-700">
                                {task.asset_number || "—"}
                              </td>

                              <td className="px-5 py-4 text-slate-600">
                                {formatDateTime(task.created_at)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </SectionCard>

              <div className="space-y-6">
                <ActionTile
                  eyebrow="Quick Action"
                  title="Report a New Maintenance Task"
                  description="Create a new operational request before it moves into assignment, cost tracking, procurement support, or closure workflow."
                  buttonText="Open Task Form"
                  onClick={() => setActiveView(DASHBOARD_VIEWS.submit)}
                  tone="emerald"
                  icon={<PlusCircle size={20} />}
                />

                <ActionTile
                  eyebrow="Management"
                  title="Open Management Workspace"
                  description="Move into the dedicated management queue to assign staff, link purchase orders, record vendor dispatch, add cost lines, and close work professionally."
                  buttonText="Open Management"
                  onClick={() => navigate("/maintenance/manage")}
                  tone="blue"
                  icon={<Wrench size={20} />}
                />

                <ActionTile
                  eyebrow="Reporting"
                  title="Review Summary and Cost Insight"
                  description="Use the maintenance reporting and cost views to monitor workload, financial movement, and operational decision support for Phase 1."
                  buttonText="Open Summary"
                  onClick={() => setActiveView(DASHBOARD_VIEWS.summary)}
                  tone="violet"
                  icon={<BarChart3 size={20} />}
                />
              </div>
            </div>
          </div>
        )}

        {activeView === DASHBOARD_VIEWS.tasks && (
          <SectionCard
            title="Task Register"
            subtitle="Review the full maintenance task list, update workflow status, and capture cost or note changes."
            rightSlot={
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => setActiveView(DASHBOARD_VIEWS.submit)} className="gap-2">
                  <PlusCircle size={15} />
                  Report New Task
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate("/maintenance/manage")}
                  className="gap-2 text-slate-900"
                >
                  Open Management
                  <ArrowRight size={15} />
                </Button>
              </div>
            }
          >
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_auto] xl:items-center">
                <TaskTableToolbar
                  filter={filter}
                  setFilter={(v) => setFilter(String(v || "all"))}
                />

                <div className="flex flex-col gap-3 sm:flex-row">
                  <div className="relative">
                    <Search
                      size={16}
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search issue, task, asset, PO, vendor..."
                      className="min-w-[290px] rounded-2xl border border-slate-300 bg-white py-2.5 pl-9 pr-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                    />
                  </div>

                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="rounded-2xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  >
                    {STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status === "all" ? "All statuses" : status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200">
                <div className="overflow-x-auto">
                  <table className="min-w-[1500px] w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-700">
                        <th className="px-4 py-3 font-semibold">Task</th>
                        <th className="px-4 py-3 font-semibold">Priority</th>
                        <th className="px-4 py-3 font-semibold">Urgency</th>
                        <th className="px-4 py-3 font-semibold">Status</th>
                        <th className="px-4 py-3 font-semibold">Request Type</th>
                        <th className="px-4 py-3 font-semibold">Asset</th>
                        <th className="px-4 py-3 font-semibold">Location</th>
                        <th className="px-4 py-3 font-semibold">Vendor</th>
                        <th className="px-4 py-3 font-semibold">PO</th>
                        <th className="px-4 py-3 text-right font-semibold">Actual Cost</th>
                        <th className="px-4 py-3 font-semibold">Reported By</th>
                        <th className="px-4 py-3 font-semibold">Update Notes</th>
                        <th className="px-4 py-3 font-semibold">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-200 bg-white">
                      {loading ? (
                        [...Array(5)].map((_, i) => <SkeletonTaskRow key={i} />)
                      ) : filteredTasks.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="px-4 py-10 text-center text-slate-500">
                            No tasks found for the current filter or search.
                          </td>
                        </tr>
                      ) : (
                        filteredTasks.map((task) => {
                          const update = updates[task.task_id] || {};
                          const statusValue = update.status ?? task.status ?? "Open";
                          const actualCostValue =
                            update.actual_cost ??
                            (task.actual_cost !== null && task.actual_cost !== undefined
                              ? String(task.actual_cost)
                              : "");

                          const isClosedLike = ["Closed", "Completed"].includes(
                            String(task.status)
                          );

                          return (
                            <tr
                              key={task.task_id}
                              className={`align-top transition hover:bg-slate-50 ${
                                isClosedLike ? "opacity-75" : ""
                              }`}
                            >
                              <td className="px-4 py-4">
                                <div className="font-medium text-slate-900">{task.issue_type}</div>
                                <div className="mt-1 text-xs text-slate-500">
                                  #{task.task_id}
                                  {task.task_number ? ` • ${task.task_number}` : ""}
                                </div>
                              </td>

                              <td className="px-4 py-4">
                                <Badge className={`border ${priorityBadgeClass(task.priority)}`}>
                                  {task.priority || "—"}
                                </Badge>
                              </td>

                              <td className="px-4 py-4">
                                <Badge className={`border ${priorityBadgeClass(task.urgency_level)}`}>
                                  {task.urgency_level || "—"}
                                </Badge>
                              </td>

                              <td className="px-4 py-4">
                                <select
                                  className="w-full rounded-xl border border-slate-300 bg-white p-2 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                  value={statusValue}
                                  onChange={(e) =>
                                    handleInlineChange(task.task_id, "status", e.target.value)
                                  }
                                >
                                  {STATUS_OPTIONS.filter((s) => s !== "all").map((status) => (
                                    <option key={status} value={status}>
                                      {status}
                                    </option>
                                  ))}
                                </select>

                                <div className="mt-2">
                                  <Badge className={`border ${statusBadgeClass(task.status)}`}>
                                    {task.status || "—"}
                                  </Badge>
                                </div>
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.request_type || "—"}
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.asset_number || task.asset_name || "—"}
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.location || "—"}
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.vendor_name || "—"}
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.po_number || "—"}
                              </td>

                              <td className="px-4 py-4 text-right">
                                <input
                                  type="number"
                                  step="0.01"
                                  className="w-28 rounded-xl border border-slate-300 bg-white px-2 py-1.5 text-right text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                  value={actualCostValue}
                                  onChange={(e) =>
                                    handleInlineChange(task.task_id, "actual_cost", e.target.value)
                                  }
                                />
                                <div className="mt-2 text-xs text-slate-500">
                                  {formatMoney(task.total_linked_cost ?? task.actual_cost)}
                                </div>
                              </td>

                              <td className="px-4 py-4 text-slate-700">
                                {task.reported_by_name || "—"}
                              </td>

                              <td className="min-w-[240px] px-4 py-4">
                                <input
                                  type="text"
                                  placeholder="Add update note..."
                                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                                  value={update.update_notes ?? ""}
                                  onChange={(e) =>
                                    handleInlineChange(
                                      task.task_id,
                                      "update_notes",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>

                              <td className="px-4 py-4">
                                <div className="flex flex-col gap-2">
                                  <TaskDetailModal
                                    taskId={task.task_id}
                                    onUpdated={() => setRefreshFlag((f) => !f)}
                                  />

                                  <Button size="sm" onClick={() => handleSave(task.task_id)}>
                                    Save
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
};

export default MaintenanceBoard;